import React, { useState, useEffect, useMemo } from "react";
import "./Experiences.css";
import BackBtnSvg from "../../assets/ReactSvg/BackBtnSvg";
import PlayIconSvg from "../../assets/ReactSvg/PlayIconSvg";
import classNames from "classnames";
import { Spin } from "antd";
import InstagramEmbed from "../InstagramEmbed/Index";
import LazyVideo from "./LazyVideo";
import { toast, ToastContainer } from "react-toastify";

const isVideoUrl = (url) =>
  typeof url === "string" &&
  (url.endsWith(".mp4") || url.endsWith(".mov") || url.endsWith(".webm"));

const Experiences = ({
  hostExperience = [],
  setViewExperiencePage,
  onViewMenu,
}) => {
  const normalizedExperience = hostExperience;

  const groupedByType = useMemo(() => {
    return normalizedExperience.reduce((acc, exp) => {
      const type = exp.creatorType || "Other";
      if (!acc[type]) acc[type] = [];
      acc[type].push(exp);
      return acc;
    }, {});
  }, [normalizedExperience]);

  const creatorTypes = Object.keys(groupedByType).filter((a) => !["Page", "User"].includes(a));
  const [activeType, setActiveType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState(0);

  const handleTypeChange = (type) => {
    if (type === activeType) return;
    setActiveType(type);
    setIsLoading(true);
    setLoadedImages(0);
  };

  useEffect(() => {
    if (!activeType && creatorTypes.length) {
      setActiveType(creatorTypes[0]);
    }
  }, [activeType, creatorTypes]);

  const activeExperiences = groupedByType[activeType]?.filter((a) => a.mediaType === "Video") || [];
  
  const allMediaUrls = useMemo(() => {
    return activeExperiences
      .filter((exp) => exp.mediaUrl)
      .map((exp) => exp.mediaUrl);
  }, [activeExperiences]);

  useEffect(() => {
    if (!allMediaUrls.length) {
      setIsLoading(false);
      return;
    }
    if (loadedImages >= allMediaUrls.length) {
      const timer = setTimeout(() => setIsLoading(false), 300);
      return () => clearTimeout(timer);
    }
  }, [loadedImages, allMediaUrls]);

  const onBookExperiences = () => {
    toast.error("Can’t book experiences as we’re in beta");

  }

  const creatorsWithExperiences = useMemo(() => {
    const seen = {};
    activeExperiences.forEach((exp) => {
      const name = exp.creatorName;
      if (!seen[name]) {
        seen[name] = {
          creatorName: exp.creatorName,
          creatorType: exp.creatorType,
          creatorBio: exp.creatorBio,
          avatar: exp.avatar,
          avatarImage: exp.avatarImage,
          experiences: [],
        };
      }
      seen[name].experiences.push(exp);
    });
    return Object.values(seen);
  }, [activeExperiences]);

  if (!normalizedExperience?.length) {
    return (
      <div className="experiences-container">
        <div className="experiences-header-wrapper">
          <div className="experiences-header">
            <button
              onClick={() => setViewExperiencePage?.(false)}
              type="button"
              className="btn back-btn experiences-header-btn"
              aria-label="Back"
            >
              <BackBtnSvg />
            </button>
            <div className="experiences-header-content Satoshi">Experiences</div>
          </div>
        </div>
        <div className="experiences-empty Satoshi">No experiences found.</div>
      </div>
    );
  }

  return (
    <div className="experiences-container">
      <div className="experiences-header-wrapper">
        <div className="experiences-header">
          <button
            onClick={() => setViewExperiencePage?.(false)}
            type="button"
            className="btn back-btn experiences-header-btn"
            aria-label="Back"
          >
            <BackBtnSvg />
          </button>
          <div className="experiences-header-content Satoshi">Experiences</div>
        </div>

        <div className="experiences-tabs">
          {creatorTypes.map((type) => (
            <span
              key={type}
              onClick={() => handleTypeChange(type)}
              className={classNames("Satoshi experiences-tab", {
                "experiences-tab-active": activeType === type,
              })}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleTypeChange(type);
              }}
            >
              {type}
            </span>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="gallery-loader">
          <Spin size="large" />
        </div>
      )}

      <div style={{ opacity: isLoading ? 0 : 1 }}>
        {creatorsWithExperiences.map((creator) => {
          const photos = creator.experiences.flatMap((exp) => {
            if (exp.mediaType === "Carousel" && exp.media) {
              return { mediaType: "Carousel", postUrl: exp.media };
            }
            return exp.mediaUrl ? [exp.mediaUrl] : [];
          });
          const avatarUrl =
            creator.avatar ||
            creator.avatarImage ||
            (Array.isArray(photos)
              ? photos.find((p) => typeof p === "string" && !isVideoUrl(p))
              : null);

          return (
            <div className="creator-profile-wrapper" key={creator.creatorName}>
              <div className="experiences-profile">
                <div className="experiences-profile-gradient" />
                <div className="experiences-profile-card">
                  <div className="experiences-profile-row">
                    {/* <div className="experiences-avatar">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={creator.creatorName} />
                      ) : (
                        <div className="experiences-avatar-fallback" aria-hidden="true" />
                      )}
                    </div> */}
                    <div className="experiences-profile-text">
                      <div className="experiences-profile-name Satoshi">
                        {creator.creatorName}
                      </div>
                      <div className="experiences-profile-role Satoshi">
                        {creator.creatorType}
                      </div>
                    </div>
                  </div>
                  <div className="experiences-profile-bio Satoshi">
                    {creator.creatorBio}
                  </div>
                </div>
              </div>

              <div className="experiences-content">
                <div className="experiences-section">
                  <div className="experiences-section-title Satoshi">
                    Dinner for two
                  </div>
                  <div className="experiences-row">
                    {photos.map((item, index) => {
                      const isCarousel = item?.mediaType === "Carousel";
                      if (isCarousel) {
                        return (
                          <div
                            key={`carousel-${index}`}
                            className="experiences-carousel-card"
                          >
                            <InstagramEmbed url={item.postUrl} />
                          </div>
                        );
                      }
                      const url = typeof item === "string" ? item : item?.url;
                      const isVideo = isVideoUrl(url);
                      return (
                        <div
                          key={`${url || "item"}-${index}`}
                          className="experiences-media-card"
                        >
                            {isVideo ? (
                             <div className="experiences-video">
                               {/* <video
                                 src={url}
                                 className="experiences-media"
                                 playsInline
                                 muted
                                 controls
                                 autoPlay
                                 onLoadedData={() =>
                                   setLoadedImages((prev) => prev + 1)
                                 }
                               /> */}
                               <LazyVideo
                                 src={url}
                                 // poster={item?.thumbnail || ""}
                                 // poster={url}
                                 onLoadedData={() =>
                                   setLoadedImages((prev) => prev + 1)
                                 }
                                 // className={`hostDineinMedia hostDineinMediaVideo`}
                                 className="experiences-media"
                               />
                               <div className="experiences-video-play-icon">
                                 <PlayIconSvg />
                               </div>
                             </div>
                           ) : (
                            <img
                              src={url}
                              alt="experience"
                              className="experiences-media"
                              onLoad={() =>
                                setLoadedImages((prev) => prev + 1)
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* <div className="experiences-bottom-bar">
        <button
          type="button"
          className="experiences-book-btn Satoshi"
          onClick={onBookExperiences}
        >
          Book experiences
        </button>
      </div> */}
      <ToastContainer />
    </div>
  );
};

export default Experiences;
